export const BLOG_POSTS = [
  {
    slug: "locksmith-dispatch-software",
    title: "Locksmith Dispatch Software: 6 Must-Haves Before You Buy",
    category: "Buying Guide",
    date: "2026-06-15",
    readMins: 7,
    img: "/marketing/fieldz-ai-dispatch-timeline.webp",
    imgAlt: "Locksmith dispatch software timeline in Fieldz AI",
    excerpt: "Lockouts don't wait for callbacks. Here are the six capabilities that separate locksmith dispatch software that wins jobs from software that just logs them.",
    sections: [
      {
        h2: "1. Sub-minute dispatch from an inbound call",
        paras: [
          "In locksmith work, the customer standing outside their car calls until someone answers and commits. That means the winning workflow is: answer, see availability, dispatch — in under a minute. Evaluate any locksmith dispatch software by literally timing this flow in the trial: incoming call, customer created, job booked, technician notified.",
          "A timeline view of every technician's day is what makes this fast. If the dispatcher has to open three screens to find who's free near the caller, you'll lose the race often enough for it to show up in revenue.",
        ],
      },
      {
        h2: "2. A phone system that's part of the product",
        paras: [
          "Locksmith businesses live on the phone, so software with a bolted-on or third-party phone system creates a permanent seam: calls in one tool, jobs in another. Look for dispatch software with native business lines — incoming calls should pop the customer's history, missed calls should surface on the dashboard, and every call should log itself against the customer automatically.",
        ],
      },
      {
        h2: "3. Call masking for your technicians",
        paras: [
          "This one is existential for locksmiths. When a tech calls ahead from a personal cell, the customer saves that number — and the next lockout may go straight to the tech, off your books. PIN-based call masking routes tech-to-customer calls through your business number, so relationships and callbacks stay with the company. If a platform treats masking as an enterprise add-on, price that in.",
        ],
      },
      {
        h2: "4. Payment collection at the door",
        paras: [
          "Lockout jobs are small-ticket and same-day — exactly the jobs where mailed invoices die. Your techs need to charge a card on-site from their phone or text a secure pay link before driving off. Bonus points if the payment automatically closes out the invoice and updates the job's financials without office work.",
        ],
      },
      {
        h2: "5. Commission tracking that ends the Friday argument",
        paras: [
          "Most locksmith shops pay techs a revenue split, and most track it in spreadsheets that are wrong by Thursday. Look for per-job commission calculation, a live owed-versus-settled balance per technician, and one-click settlements. Techs should be able to see their own ledger — transparency kills disputes.",
        ],
      },
      {
        h2: "6. Multi-location support you won't outgrow",
        paras: [
          "Successful locksmith operations tend to expand city by city. If that's your trajectory, verify the software supports per-branch phone lines, location-scoped permissions for managers, and consolidated reporting — before you're locked into a platform that treats a second location as an enterprise sales conversation.",
          "Fieldz AI was built around exactly this checklist — dispatch timeline, native phone with masking, field payments, commission settlements, and multi-location plans. Run a week of real lockouts through the free trial and time the workflow yourself.",
        ],
      },
    ],
    takeaways: [
      "Time the call-to-dispatch flow in any trial — under a minute wins jobs",
      "Call masking keeps customer relationships with your business, not your techs",
      "Demand per-tech commission ledgers and one-click settlements",
    ],
    relatedFeatures: ["dispatch", "call-masking", "payments"],
    relatedIndustries: ["locksmith"],
  },
  {
    slug: "hvac-scheduling-software",
    title: "HVAC Scheduling Software: How to Survive Peak Season Without Overbooking",
    category: "Buying Guide",
    date: "2026-06-12",
    readMins: 8,
    img: "/marketing/fieldz-ai-job-scheduling-calendar.webp",
    imgAlt: "HVAC scheduling software calendar in Fieldz AI",
    excerpt: "The first heat wave doubles your call volume overnight. Here's how HVAC scheduling software keeps capacity honest when the phones melt down.",
    sections: [
      {
        h2: "Why peak season breaks manual scheduling",
        paras: [
          "An HVAC calendar that works fine in April fails in July for one reason: invisible capacity. When bookings live on a whiteboard or a shared spreadsheet, nobody can see the difference between 'busy' and 'impossible' — so dispatchers overbook, techs run 90 minutes late, and the day ends with angry callbacks.",
          "HVAC scheduling software fixes this by making every technician's day visible at booking time. The person answering the phone sees real gaps, not guesses, and promises windows the team can actually hit.",
        ],
      },
      {
        h2: "The capacity view: your peak-season dashboard",
        paras: [
          "Look for a calendar with day and week views filterable by technician, plus a timeline that lays every tech's jobs side by side. During a heat wave, the timeline becomes mission control: you can spot the tech with a cancelled 2pm, slot the no-cool emergency in, and keep the rest of the board intact.",
          "Color-coded statuses matter more than they sound — when every job shows scheduled, en-route, in-progress, or completed at a glance, dispatchers stop calling techs to ask where they are.",
        ],
      },
      {
        h2: "Recurring maintenance is your shoulder-season revenue",
        paras: [
          "Spring and fall tune-ups smooth the revenue curve, but only if they get booked. Scheduling software with recurring jobs and automated reminders turns your maintenance list into a self-filling calendar: the visit generates automatically, the customer gets an SMS reminder, and reschedules move a single occurrence without breaking the series.",
        ],
      },
      {
        h2: "Reminders cut the no-shows you can't afford",
        paras: [
          "In peak season, a no-show isn't just lost revenue — it's a wasted slot ten other customers wanted. A day-before SMS reminder plus a morning-of confirmation dramatically cuts silent no-shows, and replies land in a two-way inbox where the office can rebook conflicts before the truck rolls.",
        ],
      },
      {
        h2: "Quote replacements on the same visit",
        paras: [
          "When a 15-year-old condenser dies, the sale happens on the driveway or not at all. Scheduling software that includes on-site estimates — built from your saved equipment library, signed on the tech's phone — converts diagnostic visits into installs without a second trip.",
          "That's the standard we built Fieldz AI around: visible capacity, recurring maintenance, automated reminders, and same-visit quoting in one platform. Peak season is coming either way — the trial is free for 7 days.",
        ],
      },
    ],
    takeaways: [
      "Overbooking comes from invisible capacity — fix it with timeline scheduling",
      "Recurring jobs + automated reminders fill shoulder-season revenue automatically",
      "Same-visit estimates convert diagnostics into installs before competitors quote",
    ],
    relatedFeatures: ["job-scheduling", "automations", "estimates"],
    relatedIndustries: ["hvac"],
  },
  {
    slug: "plumbing-invoicing-software",
    title: "Plumbing Invoicing Software: Get Paid Before You Leave the Driveway",
    category: "Finance",
    date: "2026-06-10",
    readMins: 6,
    img: "/marketing/fieldz-ai-invoice-software.webp",
    imgAlt: "Plumbing invoicing software with online payments in Fieldz AI",
    excerpt: "Plumbers who invoice from the driveway get paid in hours. Plumbers who invoice from the office on Friday get paid in weeks. Here's how to move the moment of payment.",
    sections: [
      {
        h2: "The cash-flow gap is a workflow problem",
        paras: [
          "Most plumbing businesses don't have a collections problem — they have a timing problem. The work is done Tuesday, the invoice goes out Friday, the customer opens it Monday, mails a check Thursday. That's ten days of float on money you already earned, multiplied across every job of the month.",
          "Plumbing invoicing software closes the gap by moving invoicing to the jobsite: the plumber finishes, taps 'convert estimate to invoice,' and takes payment on the spot.",
        ],
      },
      {
        h2: "What jobsite invoicing actually requires",
        paras: [
          "Three things have to be true. First, the technician app must build real invoices — your saved line items, discounts, and correct tax by service address, not a text message saying 'invoice to follow.' Second, card payment has to work in the field, with the amount prefilled and the money routed to the business account. Third, the office must see it instantly: invoice, payment, and job financials updating in real time.",
        ],
      },
      {
        h2: "Text-to-pay for everyone else",
        paras: [
          "Some customers aren't home — tenants, landlords, warranty companies. For them, the winning move is a text with a secure payment link the moment the job completes. A text gets opened in minutes; an emailed PDF waits for a printer. One tap to a Stripe checkout, no login, and the invoice marks itself paid.",
        ],
      },
      {
        h2: "Deposits protect the big jobs",
        paras: [
          "Repipes and remodels deserve different treatment: a deposit collected the moment the estimate is e-signed. It funds materials, confirms commitment, and turns one large receivable into two smaller ones. Look for invoicing software where deposit requests are part of the estimate approval flow, not a separate awkward phone call.",
          "Fieldz AI ships all of this — field invoicing, on-site card payments, text-to-pay, deposits, and automatic receipts — on every plan. Try it on your next five jobs and watch the average days-to-paid collapse.",
        ],
      },
    ],
    takeaways: [
      "Move invoicing to the jobsite — the willingness to pay peaks when the water's back on",
      "Text-to-pay links beat emailed PDFs by days, not hours",
      "Collect deposits on signed estimates for repipes and remodels",
    ],
    relatedFeatures: ["invoicing", "payments", "technician-app"],
    relatedIndustries: ["plumbing"],
  },
  {
    slug: "electrician-estimating-software",
    title: "Electrician Estimating Software: Quote Faster, Win More Jobs",
    category: "Buying Guide",
    date: "2026-06-08",
    readMins: 6,
    img: "/marketing/fieldz-ai-technician-portal.webp",
    imgAlt: "Electrician creating an estimate in the Fieldz AI technician app",
    excerpt: "The first professional quote usually wins the electrical job. Estimating software decides whether that quote is yours — sent from the driveway — or your competitor's, sent Thursday.",
    sections: [
      {
        h2: "Speed-to-quote is a conversion rate",
        paras: [
          "Homeowners collecting quotes for a panel upgrade typically call two or three electricians. The one who sends a clean, professional estimate first sets the anchor price and wins a disproportionate share — often before the others have even visited. Electrician estimating software is really conversion software: its job is to compress 'we'll get you a quote' from days to minutes.",
        ],
      },
      {
        h2: "A price book beats a blank page",
        paras: [
          "Fast quotes come from a saved item library: panel upgrades, circuit runs, EV charger installs, fixture swaps — each with your price, description, and cost. On-site, the electrician assembles the estimate from the library, adds custom lines for the weird stuff, and applies discounts per line or per document. No retyping, no math errors, no 'I'll calculate it tonight.'",
        ],
      },
      {
        h2: "E-signatures close while you're still there",
        paras: [
          "An estimate the customer can sign on the electrician's phone — or from a text link an hour later — removes the silent killer of electrical sales: the stall. Signed documents should lock automatically, convert to invoices in one click, and support a deposit request so materials money arrives before the copper does.",
        ],
      },
      {
        h2: "Know which quotes actually made money",
        paras: [
          "Winning the job is half the story; the estimating loop closes when you compare quoted price to real job cost — materials, labor commissions, processing fees. Software that tracks job-level profit tells you which types of electrical work to chase and which to reprice. Over a season, that feedback loop is worth more than any single job.",
          "Fieldz AI gives electricians the full loop: a price-book estimate builder in the technician app, e-signatures, deposits, one-click invoicing, and per-job profit reporting. Quote your next site visit from the driveway — the trial is free.",
        ],
      },
    ],
    takeaways: [
      "The first professional quote anchors the price and usually wins",
      "Build estimates from a saved price book, not a blank page",
      "Close the loop: compare quoted price to real job cost per job type",
    ],
    relatedFeatures: ["estimates", "technician-app", "financial-reporting"],
    relatedIndustries: ["electrical"],
  },
  {
    slug: "reduce-no-shows-automated-reminders",
    title: "How to Reduce No-Shows with Automated Appointment Reminders",
    category: "Operations",
    date: "2026-06-02",
    readMins: 6,
    img: "/marketing/fieldz-ai-automations.webp",
    imgAlt: "Automated appointment reminders in Fieldz AI",
    excerpt: "No-shows quietly drain field service revenue. Here's a practical reminder cadence that keeps customers home when your tech arrives — without a single manual text.",
    sections: [
      {
        h2: "Why no-shows happen (and why it's rarely malice)",
        paras: [
          "Most no-shows aren't customers dodging you — they're customers who forgot. A repair booked eight days ago competes with work meetings, school pickups, and everything else in a busy life. If your only touchpoint was the original phone call, the odds the customer remembers the exact window are lower than most owners assume.",
          "The cost compounds: a missed two-hour window doesn't just lose that job's revenue. It strands a technician mid-route, pushes the rest of the day late, and often triggers an awkward rebooking call your office has to handle.",
        ],
      },
      {
        h2: "The reminder cadence that works",
        paras: [
          "Field service businesses consistently do well with a three-touch cadence. First, an instant booking confirmation the moment the job is scheduled — it validates the appointment and gives the customer something to check later. Second, a day-before reminder in the late afternoon or evening, when people plan tomorrow. Third, an optional en-route or morning-of text with the technician's name.",
          "SMS beats email for all three touches. Text messages are typically read within minutes, while service-reminder emails routinely sit unopened. Keep messages short, include the date, time window, and address on file, and always identify your business by name.",
        ],
      },
      {
        h2: "Make it automatic or it won't happen",
        paras: [
          "A cadence that depends on your dispatcher remembering to text 30 customers a day will fail the first busy week. The fix is event-driven automation: reminders that trigger from the job record itself. When the job is booked, the confirmation fires. The day before the scheduled time, the reminder fires. Nobody has to remember anything.",
          "In Fieldz AI, these live under Automations: pick the trigger, edit the template with placeholders like the customer's name and appointment time, and turn it on. From then on, every job on the calendar gets the cadence automatically — including jobs booked by a technician from the field.",
        ],
      },
      {
        h2: "Handle the reschedules gracefully",
        paras: [
          "A good reminder doesn't just prevent no-shows — it surfaces conflicts early. A customer who replies 'can we do Thursday instead?' the evening before is a five-minute fix. The same customer discovered as a no-show at 9am is a wasted truck roll.",
          "Because replies land in your two-way SMS inbox tied to the customer record, your office sees the request with full context and can drag the job to Thursday on the calendar. The customer automatically gets a new confirmation for the updated time.",
        ],
      },
      {
        h2: "Measure it",
        paras: [
          "Track two numbers before and after turning reminders on: jobs marked cancelled or no-show per week, and same-day reschedules. Most businesses see missed appointments drop sharply within the first month, and the reschedules that do happen move earlier — where they're cheap to absorb.",
        ],
      },
    ],
    takeaways: [
      "Use a three-touch SMS cadence: booking confirmation, day-before reminder, en-route notice",
      "Automate reminders from job events — manual texting fails under load",
      "Two-way SMS turns would-be no-shows into early reschedules",
    ],
    relatedFeatures: ["automations", "sms-messaging", "job-scheduling"],
  },
  {
    slug: "field-service-kpis-that-matter",
    title: "7 KPIs Every Field Service Business Should Track",
    category: "Finance",
    date: "2026-05-19",
    readMins: 8,
    img: "/marketing/fieldz-ai-financial-reports.webp",
    imgAlt: "Field service financial KPIs in Fieldz AI",
    excerpt: "Revenue tells you you're busy. These seven numbers tell you whether you're actually making money — and where it's leaking.",
    sections: [
      {
        h2: "1. Job-level gross profit",
        paras: [
          "Revenue per job minus parts, technician commission, and payment processing fees. This is the single most clarifying number in field service: two jobs with identical $600 tickets can net wildly different profit once a $220 part and a 40% commission enter the math. If you only watch top-line revenue, high-cost jobs hide inside good months.",
        ],
      },
      {
        h2: "2. Operating result (net of expenses)",
        paras: [
          "Take your completed-jobs profit and subtract business expenses — fuel, insurance, software, rent. This is the money the business actually made. Tracking it weekly instead of discovering it at tax time lets you react while it still matters.",
        ],
      },
      {
        h2: "3. Technician commission liability",
        paras: [
          "How much do you currently owe each technician? Businesses that track this in spreadsheets almost always discover discrepancies — missed jobs, double-counted bonuses, stale rates. A live owed-versus-settled ledger per tech removes the Friday-afternoon argument and the accounting risk at once.",
        ],
      },
      {
        h2: "4. Outstanding balance (accounts receivable)",
        paras: [
          "The sum of sent-but-unpaid invoices. Watch both the total and its age: an outstanding balance that grows faster than revenue means your collection process, not your sales, is the bottleneck. Text-to-pay links and automatic receipt flows pull this number down fast.",
        ],
      },
      {
        h2: "5. Missed calls per week",
        paras: [
          "In trades where customers call the next company when nobody answers, a missed call is usually a lost job. Count them. If the number surprises you, fix routing first — ring more people, add sequential fallback, extend business hours coverage — before spending another dollar on ads.",
        ],
      },
      {
        h2: "6. Revenue by lead source",
        paras: [
          "Tag every job with where it came from: Google, referral, marketplace, repeat customer. Report revenue and profit per source quarterly. Most businesses find one channel quietly outperforming everything else — and one they're overpaying for.",
        ],
      },
      {
        h2: "7. Jobs per technician per day",
        paras: [
          "The capacity metric. If one tech consistently completes four jobs a day and another two, the difference is route density, job mix, or coaching opportunity — all fixable once visible. Combine it with job-level profit to see who's producing, not just who's busy.",
          "None of these require a finance degree — they require a system that records parts, commissions, fees, and sources on every job as it happens. That's exactly what integrated field service reporting is for.",
        ],
      },
    ],
    takeaways: [
      "Watch job-level profit, not just revenue — costs hide inside good months",
      "Keep commission liability and outstanding balances live, not monthly",
      "Missed calls and lead-source profit are marketing KPIs in disguise",
    ],
    relatedFeatures: ["financial-reporting", "payments", "lead-capture"],
  },
  {
    slug: "what-is-call-masking",
    title: "What Is Call Masking — and Why Your Technicians Need It",
    category: "Communication",
    date: "2026-05-05",
    readMins: 5,
    img: "/marketing/fieldz-ai-call-history.webp",
    imgAlt: "Call masking in Fieldz AI call history",
    excerpt: "When a technician calls a customer from a personal cell, your business hands over its most valuable asset: the relationship. Call masking fixes that.",
    sections: [
      {
        h2: "The problem with personal phones",
        paras: [
          "It starts innocently: the tech calls ahead, 'I'm 20 minutes out.' The customer saves the number. Six months later the water heater acts up again — and the customer calls the technician directly. Maybe the tech books it through your company. Maybe they do it Saturday for cash. Either way, you've lost visibility into your own customer relationship.",
          "There's a privacy cost in the other direction too. Technicians increasingly don't want customers having their personal numbers — late-night texts and unwanted callbacks are a real quality-of-life issue that pushes good techs toward employers who protect them.",
        ],
      },
      {
        h2: "How call masking works",
        paras: [
          "Call masking routes technician-to-customer calls through a number your business owns. In Fieldz AI, the tech dials their assigned masking number and enters a short PIN tied to their job. The system bridges the call to the customer — who sees your business caller ID, not the tech's cell.",
          "Because the bridge happens server-side, it works from any phone with no app required. The call also logs against the job automatically, so your office sees that contact happened without anyone filing a report.",
        ],
      },
      {
        h2: "What changes for your business",
        paras: [
          "Every callback comes to your business line — where it's answered, matched to the customer's history, and booked as a proper job. Customer relationships accrue to the company that earned them. Disputes get easier too: the communication trail lives on the job record instead of in a departed employee's phone.",
          "For technicians, it's a genuine perk: full customer contact ability with zero personal exposure. Many businesses mention masking in job ads for exactly that reason.",
        ],
      },
      {
        h2: "Setting it up",
        paras: [
          "In Fieldz AI, assign a masking number per technician or share one account-wide number. Turn masking on, and tech-facing job notifications automatically show the masked contact number instead of the customer's real one — templates use masking-aware placeholders so real numbers never leak. Setup takes minutes; the protection lasts as long as you run the business.",
        ],
      },
    ],
    takeaways: [
      "Personal-cell calls hand your customer relationships to individual techs",
      "Masking bridges calls through your business number with a job PIN",
      "Callbacks return to your line — and every call logs to the job",
    ],
    relatedFeatures: ["call-masking", "business-phone", "technician-app"],
  },
  {
    slug: "choosing-field-service-software-2026",
    title: "How to Choose Field Service Management Software in 2026",
    category: "Buying Guide",
    date: "2026-04-21",
    readMins: 9,
    img: "/marketing/fieldz-ai-field-service-dashboard.webp",
    imgAlt: "Field service management dashboard",
    excerpt: "A practical evaluation framework: the questions that actually separate platforms, the hidden costs to watch for, and how to run a trial that proves anything.",
    sections: [
      {
        h2: "Start with your workflow, not feature lists",
        paras: [
          "Every vendor's feature page looks similar. The real differences appear when you walk your actual day through the product: a call comes in — what happens? Can whoever answers see the customer's history instantly? How many clicks from that call to a scheduled, dispatched job? Write down your five most common workflows and evaluate against those, not against a checklist of 200 features you'll never open.",
        ],
      },
      {
        h2: "Count the systems you're replacing",
        paras: [
          "The strongest ROI case for modern field service software is consolidation. If you're currently paying for a scheduling tool, a VoIP phone system, an invoicing product, and a review-request service, a platform that includes all four changes the math completely — before counting the hours saved on double-entry.",
          "Ask specifically: is the phone system native or a third-party integration? Is SMS included or metered separately at painful rates? Are 'automations' actually included in the tier you'd buy?",
        ],
      },
      {
        h2: "Interrogate the pricing model",
        paras: [
          "Three pricing traps recur across the industry. Per-user pricing that makes your tenth hire cost more in software than in tools. Feature-gated tiers where the thing you need is always one tier up. And add-on stacking, where the advertised price quietly doubles once phone, SMS, and portals are bolted on.",
          "Prefer transparent public pricing, and calculate the true monthly cost for your team size with every capability you need turned on — not the teaser tier.",
        ],
      },
      {
        h2: "Check the money math",
        paras: [
          "Weak financial reporting is the most common regret cited by switchers. Verify the platform can answer: what did this job net after parts, commissions, and card fees? What do I owe each technician right now? Is sales tax tracked separately or silently inflating revenue? If the demo can't show job-level profit, your accountant will be rebuilding it in spreadsheets forever.",
        ],
      },
      {
        h2: "Run a real trial",
        paras: [
          "Free trials are only useful if you run real work through them. Import a slice of customers, book actual jobs for a week, send real invoices, and have one technician live on the mobile app. Pay attention to how often you have to leave the platform to finish a task — every exit is a seam where data and time leak.",
          "Finally, test support before you're a customer: send a question mid-trial and see how fast and how competently it's answered. That experience predicts the relationship better than any sales call.",
        ],
      },
    ],
    takeaways: [
      "Evaluate against your five most common workflows, not feature checklists",
      "Price the platform with phone, SMS, and portals included — not the teaser tier",
      "Demand job-level profit reporting; weak money math is the #1 switcher regret",
    ],
    relatedFeatures: ["financial-reporting", "business-phone", "job-scheduling"],
  },
  {
    slug: "multi-location-dispatch-playbook",
    title: "Scaling From One Location to Many: A Dispatch Playbook",
    category: "Growth",
    date: "2026-04-07",
    readMins: 7,
    img: "/marketing/fieldz-ai-multi-location-dashboard.webp",
    imgAlt: "Multi-location dashboard in Fieldz AI",
    excerpt: "Opening a second branch breaks more systems than it creates. Here's how growing field service companies structure dispatch, phones, and reporting across locations.",
    sections: [
      {
        h2: "Why the second location breaks everything",
        paras: [
          "One location runs on shared context: everyone knows the techs, the trucks, and today's chaos. The second location destroys that ambient awareness. Suddenly a dispatcher books a Aurora tech for a Boulder job, revenue mixes into one indistinguishable pot, and the new branch manager either sees everything (bad) or nothing (worse).",
          "The fix isn't heroic coordination — it's structure: location-aware jobs, phones, permissions, and reports.",
        ],
      },
      {
        h2: "Give every job a home branch",
        paras: [
          "Every job should carry a location from the moment it's created, driving which technicians are eligible, which phone line confirmations send from, and which branch's P&L the revenue lands in. In Fieldz AI, multi-location accounts pick the branch right in the job form — with the default location prefilled so speed doesn't suffer.",
        ],
      },
      {
        h2: "Phone lines per location",
        paras: [
          "Local numbers matter in local trades: customers answer local area codes and trust businesses that feel nearby. Provision a line per branch, route each line to that branch's team with business-hours rules, and keep after-hours calls flowing to voicemail or an on-call sequence. Every call still logs into one system, so ownership keeps full visibility.",
        ],
      },
      {
        h2: "Permissions: managers see their branch",
        paras: [
          "Branch managers need full operational control of their location — and no ability to browse another branch's revenue. Location-based permissions solve the trust problem structurally: owners see the consolidated picture, managers see their slice, and technicians see only their own jobs.",
        ],
      },
      {
        h2: "Report both ways: rolled up and split out",
        paras: [
          "Ownership needs two views that spreadsheets struggle to keep honest: the consolidated company view (total revenue, profit, outstanding balances) and the per-branch comparison (which location is growing, which is bleeding margin on parts). When both come from the same live job data, weekly reviews turn from data-gathering exercises into decision meetings.",
          "Get these four structures right and the third and fourth locations become repetition instead of reinvention — the point at which a field service company becomes genuinely scalable.",
        ],
      },
    ],
    takeaways: [
      "Stamp every job with a home branch — it drives dispatch, phones, and P&L",
      "Give each location a local line routed to its own team",
      "Branch managers get their slice; owners get the consolidated view",
    ],
    relatedFeatures: ["dispatch", "business-phone", "financial-reporting"],
  },
  {
    slug: "get-paid-faster-field-service",
    title: "How Field Service Businesses Get Paid Faster",
    category: "Finance",
    date: "2026-03-24",
    readMins: 6,
    img: "/marketing/fieldz-ai-invoice-software.webp",
    imgAlt: "Invoice with online payment in Fieldz AI",
    excerpt: "The gap between 'job done' and 'money in the bank' is where field service cash flow goes to die. Five changes that close it.",
    sections: [
      {
        h2: "Collect on-site by default",
        paras: [
          "The single highest-impact change: make payment part of the job completion ritual, not a back-office process that starts after the truck leaves. A technician who can charge a card from their phone — with the amount prefilled from the invoice — converts most residential jobs to paid-same-day. The psychology matters: willingness to pay peaks the moment the problem is fixed.",
        ],
      },
      {
        h2: "Text the payment link, don't mail the invoice",
        paras: [
          "For customers who don't pay on-site, the invoice should arrive as a text message with a secure pay link while the visit is still fresh. A text gets opened in minutes; a mailed invoice starts a 30-day clock and an emailed PDF often needs a printer, a checkbook, and a stamp to become money.",
          "Make the link one tap to a checkout — no account creation, no login. Every step you remove measurably increases same-week payment rates.",
        ],
      },
      {
        h2: "Take deposits on approved estimates",
        paras: [
          "For larger jobs, collect a deposit the moment the estimate is signed. It secures commitment, funds materials, and converts a single large receivable into two smaller, safer ones. With estimate-to-invoice conversion and deposit requests built into one flow, this takes seconds rather than a separate conversation.",
        ],
      },
      {
        h2: "Automate the receipt and the reminder",
        paras: [
          "Receipts should send themselves the instant payment records — it closes the loop for the customer and kills the 'did you get my payment?' call. For unpaid invoices, an automated, polite reminder at day 3 and day 7 recovers a surprising share without any awkward phone calls. Consistency beats intensity: a reliable gentle nudge outperforms an occasional stern one.",
        ],
      },
      {
        h2: "Watch the outstanding-balance number weekly",
        paras: [
          "Put total outstanding receivables on the dashboard you look at every morning. When collection is working, the number stays proportional to revenue; when it creeps, you'll catch it in days instead of discovering a cash crunch at quarter end. Businesses that instrument this number almost always shrink it — visibility alone changes behavior.",
        ],
      },
    ],
    takeaways: [
      "Make on-site card payment part of job completion, not an afterthought",
      "Text pay links immediately — one tap, no login, no PDF",
      "Deposits on signed estimates de-risk big jobs and fund materials",
    ],
    relatedFeatures: ["payments", "invoicing", "technician-app"],
  },
];

export const getPost = (slug) => BLOG_POSTS.find((p) => p.slug === slug);
